import clsx from "clsx";
import CameraView from "public/ui-elements/cameraView.svg";
import Compass from "public/ui-elements/compass.svg";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

// import { useWeather, WEATHER_STATION_ID } from "@features/weather";

// import { useWeather, WEATHER_STATION_ID } from "@features/weather";

import useValueUpdate from "@shared/hooks/use-value-update";
// import { BrandIcon } from "@shared/ui/brand-icon";
import { Icon } from "@shared/ui/icon";
import { Image } from "@shared/ui/image";
import { StyledButton } from "@shared/ui/styled-button";
import { Body, Heading } from "@shared/ui/typography";
import { waitForEdgePlayerApi } from "@shared/utils/edge-player";

import type { WebCamType } from "@/_pages/web-cams/model/types";

import s from "./web-cam-popup.module.scss";

type DetailsType = {
  length: string;
  heightDif: string;
  complexity: string;
  statusCam: string;
};

const DETAILS: DetailsType = {
  length: "Протяженность",
  heightDif: "Перепад высот",
  complexity: "Сложность",
  statusCam: "Статус",
};

const detailsItems: string[] = Object.keys(DETAILS);

export type WebCamPopupProps = ComponentProps<"div"> & {
  data: WebCamType;
  isOpen: boolean;
  onClose: () => void;
};

export const WebCamPopup = (props: WebCamPopupProps) => {
  const { data, isOpen, onClose } = props;

  const [isFullsize, setIsFullSize] = useState(false);

  const $root = useRef<HTMLDivElement | null>(null);
  const $iframe = useRef<HTMLIFrameElement | null>(null);
  const $eplayer = useRef<any | null>(null);

  const $timer = useRef<NodeJS.Timeout | null>(null);

  const toggleFullsize = useCallback(() => {
    setIsFullSize((prev) => !prev);

    if (isFullsize) {
      document.exitFullscreen();
    } else {
      $root.current?.requestFullscreen();
    }
  }, [isFullsize]);

  const handleClose = useCallback(() => {
    onClose();
    setIsFullSize(false);
    if (isFullsize) {
      document.exitFullscreen();
    }
  }, [onClose, isFullsize]);

  useEffect(() => {
    const iframe = $iframe.current;

    if (!iframe) return;

    let cancelled = false;

    // Скрипт плеера сторонний: если он не приехал, страница должна остаться
    // рабочей, просто без видео
    waitForEdgePlayerApi().then((EdgePlayerApi) => {
      if (cancelled || !EdgePlayerApi) return;

      $eplayer.current = new EdgePlayerApi(iframe);
      $eplayer.current.method({ name: "mute" });
    });

    return () => {
      cancelled = true;
      $eplayer.current?.destroy();
    };
  }, []);

  useValueUpdate(() => {
    if (!$eplayer.current) return;
    if ($timer.current) clearTimeout($timer.current);

    $eplayer.current.method({ name: "mute" });

    $eplayer.current.on("ready", () => {
      if (isOpen) {
        $eplayer.current.method({ name: "play" });
      } else {
        $eplayer.current.method({ name: "pause" });
      }
    });

    $timer.current = setTimeout(() => {
      if (isOpen) {
        $eplayer.current.method({ name: "play" });
      } else {
        $eplayer.current.method({ name: "pause" });
      }
    }, 100);
  }, isOpen);

  // const { weather } = useWeather({
  //   stationId: process.env.NEXT_PUBLIC_KONPID_STATION_ID || WEATHER_STATION_ID,
  // });

  return (
    data && (
      <div
        className={clsx(s.root, { [s.hideElements]: isFullsize })}
        ref={$root}
      >
        <div className={s.top}>
          <div className={s.texts}>
            {data.title && (
              <Body weight="semiBold" className={s.title}>
                {data.title}
              </Body>
            )}
            {!data.cameraDetail && data.description && (
              <Body size="caption" className={s.description}>
                {data.description}
              </Body>
            )}
            {data?.cameraDetail && (
              <div className={s.detailInfo}>
                {(detailsItems ?? []).map((item, idx) =>
                  item != null ? (
                    <div key={item ?? idx}>
                      <Body
                        size="uppercase-small"
                        weight="semiBold"
                        className={s.detailVal}
                      >
                        {DETAILS[item as keyof typeof DETAILS]}:
                      </Body>
                      <Body size="caption" weight="semiBold">
                        {data?.cameraDetail?.[
                          item as keyof typeof data.cameraDetail
                        ] ?? "-"}
                      </Body>
                    </div>
                  ) : null,
                )}
              </div>
            )}
          </div>
          <div className={s.buttons}>
            <StyledButton
              onClick={toggleFullsize}
              size="xs"
              className={s.cross}
              aria-label="Развернуть на весь экран"
            >
              <Icon name="big" />
            </StyledButton>
            <div className={s.live}>
              <div className={s.redDot} />
              <Body size="uppercase-small" weight="semiBold">
                В эфире
              </Body>
            </div>
            <StyledButton
              onClick={handleClose}
              size="xs"
              className={s.cross}
              aria-label="Закрыть"
            >
              <Icon name="cross" />
            </StyledButton>
          </div>
        </div>
        <div className={s.content}>
          {data.camLink && (
            <iframe
              className={s.camera}
              src={data.camLink}
              title={data.title || "camera frame"}
              width="100%"
              height="100%"
              frameBorder="0"
              scrolling="no"
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture;"
              ref={$iframe}
            />
          )}
          <div className={s.cameraInfo}>
            <div className={s.cameraInfoTop}>
              <div className={s.live}>
                <div className={s.redDot} />
                <Body size="uppercase-small" weight="semiBold">
                  В эфире
                </Body>
              </div>
              {data?.rotateCompas && (
                <Compass
                  className={s.compas}
                  style={{ "--rotate-compas": `${data.rotateCompas}deg` }}
                />
              )}
            </div>
            <div className={s.cameraDetail}>
              {/* <div className={s.weather}>
                <BrandIcon
                  name={weather?.icon || "sur"}
                  fillColor="azure"
                  strokeColor="white"
                  className={s.weatherIcon}
                />
                <div className={s.weatherInfo}>
                  {weather?.temperature && (
                    <Heading level={5}>{weather?.temperature?.full}</Heading>
                  )}
                  {weather?.description && (
                    <Body size="uppercase-small" weight="semiBold">
                      {weather?.description}
                    </Body>
                  )}
                </div>
              </div> */}
              {data?.cameraPosition?.url && (
                <div className={s.cameraMap}>
                  <Image
                    className={s.cameraPosition}
                    src={data.cameraPosition.url}
                    alt={
                      data.cameraPosition?.alternativeText ?? "camera-position"
                    }
                    height="100%"
                  />
                  <CameraView className={s.cameraView} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  );
};

WebCamPopup.displayName = "WebCamPopup";
